import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 16 },
  title: { paddingBottom: 4, "& h2": { fontWeight: 600 } },
  list: { margin: theme.spacing(1, 0, 0), paddingLeft: 18, color: theme.palette.text.secondary, lineHeight: 1.6 },
  strong: { color: theme.palette.text.primary, fontWeight: 600 },
  callout: {
    display: "flex",
    gap: theme.spacing(1.25),
    alignItems: "flex-start",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.text.primary,
  },
  actions: { padding: theme.spacing(1.5, 3, 2.5), gap: theme.spacing(1), flexWrap: "wrap" },
  button: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
  danger: {
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    "&:hover": { backgroundColor: theme.palette.error.dark },
  },
}));

// "sex., 07/11 às 14:00"
const formatWhen = (iso) => {
  const d = new Date(iso);
  const day = d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
  const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${day} às ${time}`;
};

// Confirmação de exclusão que mostra o impacto e oferece "Desativar".
const ServiceDeleteDialog = ({ service, onClose, onDeleted, onDeactivated }) => {
  const classes = useStyles();
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setImpact(null);
    if (!service) return;
    let cancelled = false;
    api
      .get(`/services/${service.id}/impact`)
      .then(({ data }) => {
        if (!cancelled) setImpact(data);
      })
      .catch(() => {
        if (!cancelled) setImpact({ futureAppointments: 0, pastAppointments: 0, nextStartAt: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [service]);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/services/${service.id}`);
      toast.success("Serviço excluído.");
      onDeleted(service.id);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const handleDeactivate = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/services/${service.id}`, { active: false });
      toast.success("Serviço desativado.");
      onDeactivated(data);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setBusy(false);
  };

  const future = impact ? impact.futureAppointments : 0;
  const past = impact ? impact.pastAppointments : 0;

  return (
    <Dialog
      open={Boolean(service)}
      onClose={() => !busy && onClose()}
      maxWidth="xs"
      fullWidth
      classes={{ paper: classes.paper }}
    >
      <DialogTitle className={classes.title}>{service ? `Excluir "${service.name}"?` : ""}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          Essa ação não pode ser desfeita.
        </Typography>
        {!impact ? (
          <div style={{ padding: "16px 0", textAlign: "center" }}>
            <CircularProgress size={22} />
          </div>
        ) : (
          <>
            <Typography variant="body2" style={{ marginTop: 12 }}>
              O que acontece ao excluir:
            </Typography>
            <ul className={classes.list}>
              {future > 0 && (
                <li>
                  <span className={classes.strong}>
                    {future} {future === 1 ? "agendamento futuro fica" : "agendamentos futuros ficam"} sem serviço
                  </span>{" "}
                  na agenda
                  {impact.nextStartAt
                    ? ` (próximo: ${formatWhen(impact.nextStartAt)})`
                    : ""}
                  .
                </li>
              )}
              {past > 0 && <li>O histórico e o financeiro passados deixam de mostrar o nome do serviço.</li>}
              <li>Sai da página pública na hora.</li>
            </ul>
            {(future > 0 || past > 0) && service && service.active && (
              <div className={classes.callout}>
                <InfoOutlinedIcon fontSize="small" color="primary" />
                <span>
                  Para só parar de oferecer o serviço, use <strong>Desativar</strong>. Ele some da página, mas a
                  agenda e o histórico continuam como estão.
                </span>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button className={classes.button} onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        {service && service.active && (
          <Button variant="outlined" className={classes.button} onClick={handleDeactivate} disabled={busy || !impact}>
            Desativar
          </Button>
        )}
        <Button
          variant="contained"
          disableElevation
          className={`${classes.button} ${classes.danger}`}
          onClick={handleDelete}
          disabled={busy || !impact}
        >
          {future > 0 ? "Excluir mesmo assim" : "Excluir"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDeleteDialog;
